<template>
  <div class="contacts-phone-list">
    <BasePhoneList
      :items="results"
      item-key="key"
      :has-more="hasMore"
      :loading-more="loadingMore"
      :load-more-error="loadMoreError"
      :pending="pending"
      @load-more="$emit('load-more')"
    >
      <template #item="{ item: result }">
        <ContactListItem :key="result.key" :result="result" @open="detailsKey = result.key" />
      </template>
    </BasePhoneList>

    <BaseBottomSheet v-model="detailsOpen" :title="title">
      <template v-if="details">
        <dl v-if="facts.length" class="base-bottom-sheet__facts">
          <div v-for="fact in facts" :key="fact.label">
            <dt>{{ fact.label }}</dt>
            <dd>{{ fact.value }}</dd>
          </div>
        </dl>

        <ContactWays v-if="person" :contact="person" @done="detailsKey = null" />
        <ul v-if="website" class="base-bottom-sheet__menu">
          <li>
            <a :href="website" target="_blank" rel="noopener noreferrer" @click="detailsKey = null">
              <BaseIcon name="globe" />
              <span class="contacts-phone-list__value">{{ details.organization.website }}</span>
            </a>
          </li>
        </ul>

        <!-- An organization's people, each with their ways to be reached. -->
        <section v-for="(contact, index) in people" :key="index" class="contacts-phone-list__person">
          <p class="contacts-phone-list__person-name">{{ contact.name }}</p>
          <ContactWays :contact="contact" @done="detailsKey = null" />
        </section>
      </template>
    </BaseBottomSheet>
  </div>
</template>

<script>
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseIcon from '@/components/base/BaseIcon';
import BasePhoneList from '@/components/base/BasePhoneList.vue';
import ContactListItem from '@/components/page-contact-directory/ContactListItem.vue';
import ContactWays from '@/components/page-contact-directory/ContactWays.vue';
import contactDirectoryScoreMixin from '@/mixins/contact-directory-score-mixin';

// The phone version of the contact directory: a row per person or organization, and a sheet
// to reach them, by mail or phone, from the phone itself.
export default {
  name: 'ContactsPhoneList',
  mixins: [contactDirectoryScoreMixin],
  components: {
    BaseBottomSheet,
    BaseIcon,
    BasePhoneList,
    ContactListItem,
    ContactWays,
  },
  props: {
    // The search results, a `key` each.
    results: {
      type: Array,
      required: true,
    },
    hasMore: {
      type: Boolean,
      default: false,
    },
    loadingMore: {
      type: Boolean,
      default: false,
    },
    loadMoreError: {
      type: [Object, Error, String],
      default: null,
    },
    pending: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      detailsKey: null,
    };
  },
  computed: {
    details() {
      return this.detailsKey === null ? null : this.results.find((result) => result.key === this.detailsKey) || null;
    },
    detailsOpen: {
      get() {
        return !!this.details;
      },
      set(open) {
        if (!open) this.detailsKey = null;
      },
    },
    isOrganization() {
      return this.details.type === 'ACCOUNT';
    },
    title() {
      if (!this.details) return '';
      return this.isOrganization ? this.details.organization.name : this.details.contactPoint.name;
    },
    // The person a result is about; an organization lists its people below instead.
    person() {
      return this.isOrganization ? null : this.details.contactPoint;
    },
    people() {
      return this.isOrganization ? this.details.organization.contactPoints || [] : [];
    },
    website() {
      const { organization } = this.details;
      if (!organization || !organization.website) return '';
      return /^https?:\/\//i.test(organization.website) ? organization.website : `https://${organization.website}`;
    },
    facts() {
      const { organization, score } = this.details;
      const label = (key) => this.$t(`page.contact-directory.phone-list.${key}`);
      return [
        !this.isOrganization && organization && { label: label('organization'), value: organization.name },
        organization && organization.address && { label: label('address'), value: organization.address.raw },
        this.person && this.person.faxNumber && { label: label('fax'), value: this.person.faxNumber },
        score != null && {
          label: 'Score',
          value: `${this.$t(`page.contact-directory.score.${this.getScoreClass(score)}`)} (${score.toFixed(0)})`,
        },
      ].filter(Boolean);
    },
  },
};
</script>

<style lang="scss">
// The sheet's parts are moved out of the list, to the app's root.
.contacts-phone-list {
  &__value {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__person {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid $color-border;
  }

  &__person-name {
    margin: 0 4px;
    font-size: 14px;
    font-weight: 600;
    color: $color-normal;
  }
}
</style>
